import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin, Subscription as RxSubscription, take } from 'rxjs';
import { APIResponse, Subscription } from 'fakturnia-shared';
import { SubscriptionsApiService, SubscriptionPlan, FeatureComparison } from 'shared/services/api/subscriptions.api.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { SubscriptionService } from 'shared/services/subscription.service';
import { SettingsService } from 'shared/services/settings.service';
import { DateTime } from 'luxon';

interface Feature {
  name: string;
  free: boolean | string;
  start: boolean | string;
  pro: boolean | string;
}

@Component({
  selector: 'app-subscriptions-list',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss']
})
export class SubscriptionsListComponent implements OnInit, OnDestroy {
  plans: SubscriptionPlan[] = [];
  features: Feature[] = [];
  isLoading = true;
  isProcessing = false;
  currentSubscription: Subscription | null = null;
  
  /** Plan podświetlony z query param (np. ?stage=1) */
  highlightedPlan: number | null = null;
  
  /** Czy użytkownik został przekierowany z powodu wygasłej subskrypcji */
  isExpiredRedirect: boolean = false;

  private _subscriptions: RxSubscription[] = [];

  constructor(
    private _subscriptionsApi: SubscriptionsApiService,
    private _subscriptionService: SubscriptionService,
    private _settingsService: SettingsService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _snackbar: SnackbarService
  ) {}

  /**
   * Sprawdza czy użytkownik ma aktywny plan FREE
   */
  get hasActiveFreePlan(): boolean {
    if (!this.currentSubscription) return false;
    
    const sub = this.currentSubscription;
    const isFreePlan = sub.stage === 0;
    
    // Sprawdź czy subskrypcja jest aktywna (validUntil w przyszłości)
    if (!sub.validUntil) return false;
    
    const validUntil = DateTime.fromISO(sub.validUntil as unknown as string);
    const isActive = validUntil > DateTime.now();
    
    return isFreePlan && isActive;
  }

  /**
   * Sprawdza czy użytkownik ma aktywny plan płatny (START, PRO, BUSINESS)
   * Jeśli tak, plan FREE nie powinien być dostępny do wyboru
   */
  get hasActivePaidPlan(): boolean {
    if (!this.currentSubscription) return false;
    
    const sub = this.currentSubscription;
    const isPaidPlan = sub.stage >= 1;
    
    // Sprawdź czy subskrypcja jest aktywna (validUntil w przyszłości)
    if (!sub.validUntil) return false;
    
    const validUntil = DateTime.fromISO(sub.validUntil as unknown as string);
    const isActive = validUntil > DateTime.now();
    
    return isPaidPlan && isActive;
  }

  /**
   * Sprawdza czy dany typ planu jest dostępny (zwrócony z API)
   */
  hasPlan(stage: number): boolean {
    return this.plans.some(p => p.stage === stage);
  }

  /**
   * Sprawdza czy subskrypcja użytkownika wygasła
   */
  get isSubscriptionExpired(): boolean {
    if (!this.currentSubscription) return true;
    
    const sub = this.currentSubscription;
    if (!sub.validUntil) return true;
    
    const validUntil = DateTime.fromISO(sub.validUntil as unknown as string);
    return validUntil <= DateTime.now();
  }

  ngOnInit(): void {
    this._loadData();
    this._loadCurrentSubscription();
    
    // Obsługa query params (?plan=x, ?expired=true)
    this._subscriptions.push(
      this._route.queryParams.subscribe(params => {
        if (params['stage']) {
          this.highlightedPlan = Number(params['stage']);
          // Scroll do podświetlonego planu po załadowaniu
          setTimeout(() => this._scrollToHighlightedPlan(), 500);
        }
        // Sprawdź czy przekierowano z powodu wygasłej subskrypcji
        if (params['expired'] === 'true') {
          this.isExpiredRedirect = true;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Sprawdza czy plan powinien być podświetlony
   */
  isPlanHighlighted(planStage: number): boolean {
    return this.highlightedPlan === planStage;
  }

  /**
   * Scrolluje do podświetlonego planu
   */
  private _scrollToHighlightedPlan(): void {
    if (!this.highlightedPlan) return;
    const element = document.getElementById(`plan-${this.highlightedPlan}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private _loadCurrentSubscription() {
    this._subscriptionService.getSubscription().pipe(take(1)).subscribe(sub => {
      this.currentSubscription = sub;
    });
  }

  private _loadData() {
    this.isLoading = true;
    
    forkJoin({
      plans: this._subscriptionsApi.getAvailablePlans().pipe(take(1)),
      features: this._subscriptionsApi.getPublicFeatureComparisons().pipe(take(1))
    }).subscribe({
      next: (responses: { plans: APIResponse; features: APIResponse }) => {
        // Plans
        if (responses.plans.success) {
          const plansData = responses.plans.data || [];
          // DEBUG: Sprawdź dane rabatowe
          console.log('[Subscriptions] Plans data:', plansData.map((p: SubscriptionPlan) => ({
            stage: p.stage,
            price: p.price,
            originalPrice: p.originalPrice,
            discountPercent: p.discountPercent,
            hasDiscount: !!(p.discountPercent && p.discountPercent > 0 && p.originalPrice)
          })));
          this.plans = plansData.sort((a: SubscriptionPlan, b: SubscriptionPlan) => {
            return (a.stage ?? 99) - (b.stage ?? 99);
          });
        }

        // Features
        if (responses.features.success) {
          const featuresData: FeatureComparison[] = responses.features.data || [];
          this.features = featuresData.map(f => ({
            name: f.name,
            free: this._normalizeFeatureValue(f.free),
            start: this._normalizeFeatureValue(f.start),
            pro: this._normalizeFeatureValue(f.pro)
          }));
        }

        this.isLoading = false;
      },
      error: (err) => {
        console.error('[Subscriptions] Error:', err);
        this.isLoading = false;
      }
    });
  }

  selectPlan(plan: SubscriptionPlan) {
    const planId = plan._id || plan.id;
    this._router.navigate(['/konto/moje-konto/subskrypcja/platnosc'], {
      queryParams: { planId }
    });
  }

  /**
   * Wybierz plan FREE - aktywuje od razu bez płatności
   */
  selectFreePlan(plan: SubscriptionPlan) {
    this.isProcessing = true;

    this._subscriptionsApi.activateFreePlan().pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isProcessing = false;
        if (response.success) {
          this._snackbar.showSnackBar('Plan FREE został aktywowany', 'success');
          // Odśwież ustawienia żeby zaktualizować dane subskrypcji w całej aplikacji
          this._settingsService.refreshSettings();
          this._router.navigate(['/konto/moje-konto/subskrypcja']);
        } else {
          this._snackbar.showSnackBar(response.message || 'Wystąpił błąd', 'warning');
        }
      },
      error: (err) => {
        this.isProcessing = false;
        this._snackbar.showSnackBar('Wystąpił błąd podczas aktywacji planu', 'warning');
        console.error('[Subscriptions] Error activating free plan:', err);
      }
    });
  }

  // Formatowanie
  formatPrice(priceInCents: number): string {
    return (priceInCents / 100).toFixed(0);
  }

  formatPriceWithDecimals(priceInCents: number): string {
    return (priceInCents / 100).toFixed(2);
  }

  getMonthlyPrice(plan: SubscriptionPlan, useOriginalPrice: boolean = false): number {
    const price = useOriginalPrice && plan.originalPrice ? plan.originalPrice : plan.price;
    // Zakładamy że cena w planie jest roczna, więc dzielimy przez 12
    if (plan.durationYears) {
      return Math.round(price / 12);
    }
    if (plan.durationMonths && plan.durationMonths > 1) {
      return Math.round(price / plan.durationMonths);
    }
    return price;
  }

  hasDiscount(plan: SubscriptionPlan): boolean {
    return !!(plan.discountPercent && plan.discountPercent > 0 && plan.originalPrice);
  }

  getTypeLabel(stage: number): string {
    const labels: Record<number, string> = {
      0: 'FREE',
      1: 'START',
      2: 'PRO',
      3: 'BUSINESS'
    };
    return labels[stage] || 'FREE';
  }

  getTypeColor(stage: number): string {
    const colors: Record<number, string> = {
      0: 'from-gray-400 to-gray-500',
      1: 'from-blue-500 to-blue-600',
      2: 'from-purple-500 to-purple-600',
      3: 'from-amber-500 to-amber-600'
    };
    return colors[stage] || 'from-gray-400 to-gray-500';
  }

  isPopular(plan: SubscriptionPlan): boolean {
    return plan.stage === 1;
  }

  getPlanByStage(stage: number): SubscriptionPlan | undefined {
    return this.plans.find(p => p.stage === stage);
  }

  getFeatureValue(feature: Feature, type: string): boolean | string {
    return (feature as any)[type];
  }

  /**
   * Normalizuje wartość feature - konwertuje stringi "true"/"false" na booleany
   */
  private _normalizeFeatureValue(value: string | boolean | undefined): string | boolean {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return value ?? false;
  }
}
